import type { Vendor } from '@urbanx/agx.campaigns.client';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AgxBodyText,
  AgxLabel,
  AgxRow,
  Images,
  PropertyAddress,
  PropertyDetails,
  PropertyImageUrls,
  useDebouncedState,
} from '@urbanx/agx-ui-components';
import clsx from 'clsx';
import { fetchAddresses, fetchPlanSuggestions } from 'Api/RpData/rpDataApi';
import { AllPropertyDetails, PropertySuggestionItem } from 'Api/RpData/Types/types';
import { PageTitle } from 'features/form/FormHeader/components/PageTitle';
import { getPropertyDetails, clearPropertyDetails } from 'features/rpData/rpDataReducer';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { setFormSettings } from 'features/form/formSettingsReducer';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useCanAccessRural } from 'hooks/useCanAccessRural';
import { LoadingState } from 'utils/loadingState';
import { PropertySearchLoading } from './Loading/PropertySearchLoading';
import { PropertyConfirmation } from './PropertyConfirmation/PropertyConfirmation';
import CannotAccessRural from './CannotAccessRural/CannotAccessRural';
import NoPropertyFound from './NoPropertyFound/NoPropertyFound';
import './PropertySearch.scss';
import ExistingPropertyFound from './ExistingPropertyFound/ExistingPropertyFound';
import { hashKey, useQuery } from '@tanstack/react-query';
import { AgxSpacer, Breakpoints, Spinner, useWindowSize } from '@urbanx/agx.ui-components2';

interface PropertySearchProps {
  id: string;
  label?: string;
  disabled?: boolean;
  regionToSearch?: string | null;
  inverseLabel?: boolean;
  defaultValue?: string;
  error?: string | null;
  onManualAddress: () => void;
  onBeginCampaign: (
    address: PropertyAddress,
    imageUrls: PropertyImageUrls,
    propertyDetails: PropertyDetails,
    vendors: Vendor[],
    hasPool: boolean
  ) => void;
  extraClasses?: string;
}

export enum Tabs {
  Address = 'Address',
  LotPlan = 'Lot/Plan',
}

enum PropertySearchSteps {
  Search = 'Search',
  PropertyLoading = 'ProperyLoading',
  PropertyConfirmation = 'PropertyConfirmation',
  NoPropertyFound = 'NoPropertyFound',
  CannotAccessRural = 'CannotAccessRural',
  ExistingPropertyAddress = 'ExistingPropertyAddress',
}

const classAugmenter = (className: string, error: string | null, disabled: boolean) => {
  return clsx(className, error && 'error', disabled && 'disabled');
};

const PropertySearch = ({
  id: addressId,
  error = null,
  label = '',
  disabled = false,
  inverseLabel = false,
  onManualAddress,
  onBeginCampaign,
}: PropertySearchProps) => {
  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const selectedForm = useAppSelector((state) => state.form.selectedForm);
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const propertyDetails = useAppSelector((state) => state.rpData.propertyDetails);

  const formState = availableForms.find(
    (form) => form.type === selectedForm?.formType && form.state === selectedForm.state
  )?.state;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState({ value: '', resetKey: 0 }, 300);
  const searchValue = debouncedSearch.value;
  const [currentTab, setCurrentTab] = useState(Tabs.Address);
  const [step, setStep] = useState(PropertySearchSteps.Search);
  const [selectedSuggestion, setSelectedSuggestion] = useState<PropertySuggestionItem | null>(null);
  const [loadPlanPropertiesSet, setLoadPlanPropertiesSet] = useState<number>(0);
  const [selectedAddress, setSelectedAddress] = useState<PropertyAddress | null>(null);
  const [focusedResultIndex, setFocusedResultIndex] = useState<number>(-1);
  const [spinnerQueryHash, setSpinnerQueryHash] = useState<string | null>(null);

  const canAccessRural = useCanAccessRural();

  const hasSearchTerm = searchTerm.replace(/\s/g, '').length >= 3;
  const queryKey = ['property-suggestion', currentTab, searchValue, formState, searchResetKey];
  const queryHash = hashKey(queryKey);
  const {
    data: propertySuggestions = [],
    isFetching,
    isSuccess,
    isError,
  } = useQuery<PropertySuggestionItem[]>({
    queryKey,
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery?.queryKey[4] !== searchResetKey) return undefined;
      return previousData;
    },
    enabled:
      hasSearchTerm &&
      debouncedSearch.resetKey === searchResetKey &&
      searchValue.replace(/\s/g, '').length >= 3 &&
      !!getAuthToken &&
      !!formState,
    queryFn: async ({ signal }) => {
      if (!getAuthToken || !formState) return [];

      const authToken = await getAuthToken();

      if (!authToken) return [];

      if (currentTab === Tabs.Address) {
        const result = await fetchAddresses(authToken, searchValue, formState, signal);
        return result?.suggestions ?? [];
      }

      const result = await fetchPlanSuggestions(authToken, searchValue, formState, signal);
      return result?.suggestions ?? [];
    },
  });

  const isSearching = hasSearchTerm && (searchTerm !== searchValue || isFetching);
  const showSearchSpinner = hasSearchTerm && isFetching && spinnerQueryHash === queryHash;
  let suggestions: PropertySuggestionItem[] = [];
  if (hasSearchTerm && !showSearchSpinner && isSuccess) {
    suggestions = propertySuggestions;
  }
  const hasNoResults = hasSearchTerm && !isSearching && isSuccess && suggestions.length === 0;

  useEffect(() => {
    setSpinnerQueryHash(null);
    if (!isFetching) return;

    const timeout = setTimeout(() => setSpinnerQueryHash(queryHash), 500);
    return () => clearTimeout(timeout);
  }, [isFetching, queryHash]);

  const updateSearchTerm = (value: string) => {
    let resetKey = searchResetKey;
    if (value.trim() === '') {
      // Discard retained results and pending debounces, even when the same address is retyped.
      resetKey += 1;
    }
    setSearchTerm(value);
    setSearchResetKey(resetKey);
    setDebouncedSearch({ value, resetKey });
    setFocusedResultIndex(-1);
  };

  const searchAgain = () => {
    dispatch(clearPropertyDetails());
    updateSearchTerm('');
    setStep(PropertySearchSteps.Search);
  };

  const loadPropertyDetails = async (suggestion: PropertySuggestionItem, page: number) => {
    if (propertyDetails.loadingState === LoadingState.Loading || !getAuthToken) return;

    const authToken = await getAuthToken();
    if (!authToken) return;

    const propertyIds = suggestion.suggestionProperties
      .slice(page * 10, (page + 1) * 10)
      .map(({ propertyId }) => propertyId);
    const items = await dispatch(getPropertyDetails(authToken, propertyIds));

    setStep((currentStep) => {
      if (currentStep === PropertySearchSteps.Search) return currentStep;
      if (!items) return PropertySearchSteps.NoPropertyFound;
      if (page > 0) return currentStep;
      if (items.length === 0) return PropertySearchSteps.NoPropertyFound;
      if (items.some((item) => item.hasValidCampaignForProperty)) {
        return PropertySearchSteps.ExistingPropertyAddress;
      }
      return PropertySearchSteps.PropertyConfirmation;
    });
  };

  const selectSuggestion = (suggestion: PropertySuggestionItem) => {
    setSelectedSuggestion(suggestion);
    setLoadPlanPropertiesSet(0);
    updateSearchTerm('');
    setStep(PropertySearchSteps.PropertyLoading);
    void loadPropertyDetails(suggestion, 0);
  };

  const loadMore = () => {
    if (!selectedSuggestion || propertyDetails.loadingState === LoadingState.Loading) return;
    const page = loadPlanPropertiesSet + 1;
    setLoadPlanPropertiesSet(page);
    void loadPropertyDetails(selectedSuggestion, page);
  };

  useEffect(() => {
    dispatch(clearPropertyDetails());
    return () => {
      dispatch(clearPropertyDetails());
    };
  }, [dispatch]);

  const wrapperClasses = clsx('wrapper', error && 'error');
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (focusedResultIndex < suggestions.length - 1) {
        setFocusedResultIndex(focusedResultIndex + 1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (focusedResultIndex > 0) {
        setFocusedResultIndex(focusedResultIndex - 1);
      } else {
        setFocusedResultIndex(-1);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedResultIndex < suggestions.length && focusedResultIndex >= 0) {
        selectSuggestion(suggestions[focusedResultIndex]);
      } else if (focusedResultIndex === -1 && suggestions.length > 0) {
        selectSuggestion(suggestions[0]);
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') updateSearchTerm('');
  };

  useEffect(() => {
    dispatch(
      setFormSettings({
        growContent: step === PropertySearchSteps.PropertyLoading,
        noGap: false,
      })
    );
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [dispatch, step]);

  const onPropertyConfirmed = (allPropertyDetails: AllPropertyDetails) => {
    if (disabled) return;

    const {
      propertyDetails,
      propertyImageUrls,
      matchedAddress: address,
      vendors,
      hasPool,
    } = allPropertyDetails;

    // Validating if it can access rural property
    if (!canAccessRural(propertyDetails.landAreaDetails, address)) {
      setSelectedAddress(address);
      setStep(PropertySearchSteps.CannotAccessRural);
      return;
    }

    onBeginCampaign(address, propertyImageUrls, propertyDetails, vendors, hasPool);
  };

  const windowSize = useWindowSize();

  const getPlaceholder = () => {
    if (windowSize === Breakpoints.Mobile) return 'Search';

    if (currentTab === Tabs.Address) {
      return 'Search address as it appears in RP Data';
    } else if (currentTab === Tabs.LotPlan) {
      return 'Search lot and/or plan eg. 10/SP244179';
    }
  };

  const renderAutoCompleteBody = () => {
    if (!hasSearchTerm) return;

    if (showSearchSpinner) {
      return (
        <AgxSpacer contentAlignment="center" gap="spacing-lg" padding="spacing-3xl" role="status">
          <Spinner size={32} color="var(--brand-text-primary-color)" />
          <AgxBodyText medium inverse centered>
            Searching properties…
          </AgxBodyText>
        </AgxSpacer>
      );
    }

    if (!isSearching && isError) {
      return (
        <AgxSpacer padding="spacing-3xl" role="alert">
          <AgxBodyText medium inverse centered>
            Unable to search properties. Please try again.
          </AgxBodyText>
        </AgxSpacer>
      );
    }

    if (hasNoResults) {
      return (
        <AgxSpacer
          contentAlignment="center"
          padding={{ top: 'spacing-6xl', bottom: 'spacing-6xl' }}
          role="status"
        >
          <AgxBodyText medium inverse centered>
            <strong>
              {currentTab === Tabs.Address
                ? 'No properties match this address'
                : 'No properties match this lot/plan'}
            </strong>
          </AgxBodyText>
        </AgxSpacer>
      );
    }

    return suggestions.map((suggestion, index) => (
      <div
        className={clsx('property-search-result', index === focusedResultIndex && 'focused')}
        key={`suggestion-${index}`}
        onClick={() => {
          selectSuggestion(suggestion);
        }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            selectSuggestion(suggestion);
          }
        }}
        onFocus={() => setFocusedResultIndex(index)}
        onBlur={() => setFocusedResultIndex(-1)}
      >
        <Images.MapPin />
        <div className="property-search-result-detail">
          <p className="propertyAddress">{suggestion.suggestionLine1}</p>
          <p className="propertyRegion">{suggestion.suggestionLine2}</p>
        </div>
      </div>
    ));
  };

  const onManualAddressClick = () => {
    if (disabled) return;
    onManualAddress();
  };

  const tabSelectors = [Tabs.Address, Tabs.LotPlan].map((tab) => {
    const classNames = clsx(
      'tabSelector',
      'cursorPointer',
      currentTab !== tab && 'body small',
      currentTab === tab && 'ui-strong-small selected'
    );

    return (
      <div
        key={tab}
        className={classNames}
        onClick={() => {
          if (currentTab === tab) return;
          updateSearchTerm('');
          setCurrentTab(tab);
          searchInputRef.current?.focus();
        }}
      >
        <AgxBodyText small>{tab}</AgxBodyText>
      </div>
    );
  });

  if (step === PropertySearchSteps.PropertyLoading) return <PropertySearchLoading />;

  if (step === PropertySearchSteps.ExistingPropertyAddress)
    return (
      <ExistingPropertyFound
        goToSearch={searchAgain}
        goToDashboard={() => {
          navigate('/');
        }}
      />
    );

  if (step === PropertySearchSteps.PropertyConfirmation)
    return (
      <PropertyConfirmation
        confirmationId={addressId}
        currentTab={currentTab}
        suggestion={selectedSuggestion}
        onPropertyConfirmed={onPropertyConfirmed}
        onSearchAgain={searchAgain}
        onLoadMore={loadMore}
        allPropertyDetails={propertyDetails.items}
        disabled={disabled}
        loading={propertyDetails.loadingState === LoadingState.Loading}
      />
    );

  if (step === PropertySearchSteps.NoPropertyFound) {
    return <NoPropertyFound goToSearch={searchAgain} goToManualAddress={onManualAddressClick} />;
  }

  if (step === PropertySearchSteps.CannotAccessRural) {
    return (
      <CannotAccessRural
        address={selectedAddress}
        onCancel={() => {
          navigate('/');
        }}
        onSearchAgain={searchAgain}
      />
    );
  }

  return (
    <>
      <PageTitle onClick={() => void 0} title="Search for Property" breadcrumbsAvailable={false} />
      <div key={addressId} className="propertySearchAutoCompleteContainer">
        <div className="textInput">
          {label && (
            <div className="header">
              {label && (
                <AgxLabel medium inverse={inverseLabel}>
                  {label}
                </AgxLabel>
              )}
            </div>
          )}
        </div>
        <div className={classAugmenter('textInput addressDropdown dark stretch', error, disabled)}>
          <div className={wrapperClasses}>
            <Images.SearchOutline />
            <input
              ref={searchInputRef}
              className="field inputText medium"
              id={addressId}
              data-testid={`agxAutoAddress-${addressId}`}
              onChange={({ target: { value } }) => updateSearchTerm(value)}
              placeholder={getPlaceholder()}
              value={searchTerm}
              disabled={disabled}
              autoComplete="off"
              inputMode="search"
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              autoFocus
            />
          </div>
          <div className="tabsContainer">
            <AgxRow largeGap>{tabSelectors}</AgxRow>
          </div>
          {error && (
            <div className="errorMessage">
              <Images.AlertCircle />
              <AgxBodyText small extraClasses="error" dataTestId={`error-${addressId}`}>
                {error}
              </AgxBodyText>
            </div>
          )}
          <div className="property-search-results">{renderAutoCompleteBody()}</div>
        </div>
      </div>
      {!showSearchSpinner && (
        <AgxBodyText medium inverse extraClasses="enterAddressManuallyText">
          Can't find what you're looking for?{' '}
          <button className="enterAddressManuallyLink" onClick={onManualAddressClick}>
            Enter address manually
          </button>
        </AgxBodyText>
      )}
    </>
  );
};

export default PropertySearch;
